/**
 * Gate-centric review model — single source of truth for how pipeline
 * stages map onto reviewer-facing roles and which upstream agents each
 * intermediate gate rolls up.
 *
 * - Agent stages are notes-only inputs.
 * - Intermediate gates (Gate 1 / Gate 2) carry the group decision and
 *   summarise their upstream agents + roll up those agents' notes.
 * - The final gate hosts the binding activation decision (FR-077).
 */
import type { PausedStage } from '@/types/enums';

export type StageRole =
  | 'summary'
  | 'agent'
  | 'intermediate_gate'
  | 'final_gate'
  | 'report';

/** Which agents feed each intermediate gate. Order = display order. */
export const GATE_GROUPS = {
  gate_1: ['document_assistant', 'document', 'parties', 'clause'],
  gate_2: ['compliance', 'signature'],
} as const satisfies Record<string, readonly PausedStage[]>;

export type IntermediateGate = keyof typeof GATE_GROUPS;

export function isIntermediateGate(stage: string): stage is IntermediateGate {
  return stage === 'gate_1' || stage === 'gate_2';
}

/** Classify a tab/stage key into its reviewer-facing role. */
export function stageRole(stage: string): StageRole {
  if (stage === 'summary') return 'summary';
  if (isIntermediateGate(stage)) return 'intermediate_gate';
  if (stage === 'gate') return 'final_gate';
  if (stage === 'orchestrator_bootstrap') return 'report';
  // The six agents + the legacy `orchestrator` stage.
  return 'agent';
}

/** Upstream agent stages for an intermediate gate (empty for anything else). */
export function upstreamStagesFor(gate: string): readonly PausedStage[] {
  return isIntermediateGate(gate) ? GATE_GROUPS[gate] : [];
}

/** The intermediate gate a stage's reviewer notes roll up into, or null. */
export function gateForStage(stage: string): IntermediateGate | null {
  for (const g of ['gate_1', 'gate_2'] as const) {
    if ((GATE_GROUPS[g] as readonly string[]).includes(stage)) return g;
  }
  return null;
}
